/**
 * Secure password change for auditors.
 *
 * The login password is also the key-encryption password. Changing it must:
 *   1. Prove current password via Firebase reauthentication
 *   2. Decrypt the existing private key locally (never sent to our backend)
 *   3. Re-wrap under the new password (new salt/IV; same keypair / publicKey)
 *   4. Persist the new wrap to Firestore, then update Firebase Auth
 *   5. Roll back the Firestore wrap if Auth update fails
 *
 * No "forgot password" path: email reset would unlock Auth without the ability
 * to decrypt encPrivKey.
 */

import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  decryptPrivateKey,
  verifyKeyPair,
  wrapPrivateKeyWithPassword,
  getPrivateKeyFromSession,
  storePrivateKeyInSession,
} from './cryptoKeyService';

const AUDITORS_COLLECTION = 'auditors';
const MIN_PASSWORD_LENGTH = 6;

function assertPasswordPolicy(password, label = 'Password') {
  if (!password || typeof password !== 'string') {
    throw new Error(`${label} is required.`);
  }
  if (password.length < MIN_PASSWORD_LENGTH) {
    throw new Error(`${label} must be at least ${MIN_PASSWORD_LENGTH} characters.`);
  }
}

function mapAuthError(error) {
  const code = error?.code || '';
  if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
    return new Error('Current password is incorrect.');
  }
  if (code === 'auth/too-many-requests') {
    return new Error('Too many attempts. Please wait a moment and try again.');
  }
  if (code === 'auth/requires-recent-login') {
    return new Error('For security, please log out and log back in, then try again.');
  }
  if (code === 'auth/weak-password') {
    return new Error('New password is too weak. Choose a longer or stronger password.');
  }
  if (code === 'auth/network-request-failed') {
    return new Error('Network error. Please check your connection and try again.');
  }
  return new Error(error?.message || 'Failed to change password. Please try again.');
}

/**
 * Change the signed-in auditor's password and re-wrap their private key.
 *
 * @param {{
 *   user: import('firebase/auth').User,
 *   auditorDocId: string,
 *   keyMaterial: {
 *     publicKey: string,
 *     encPrivKey: string,
 *     salt: string,
 *     kdfParams: Object,
 *     iv: string,
 *     keyVersion?: number,
 *   },
 *   currentPassword: string,
 *   newPassword: string,
 * }} params
 * @returns {Promise<{
 *   encPrivKey: string,
 *   salt: string,
 *   kdfParams: Object,
 *   iv: string,
 *   keyVersion: number,
 *   updatedAt: string,
 * }>}
 */
export async function changeAuditorPassword({
  user,
  auditorDocId,
  keyMaterial,
  currentPassword,
  newPassword,
}) {
  if (!user?.email) {
    throw new Error('You must be signed in to change your password.');
  }
  if (!auditorDocId) {
    throw new Error('Something went wrong. Please try again.');
  }

  assertPasswordPolicy(currentPassword, 'Current password');
  assertPasswordPolicy(newPassword, 'New password');

  if (currentPassword === newPassword) {
    throw new Error('New password must be different from your current password.');
  }

  if (!keyMaterial?.encPrivKey || !keyMaterial?.publicKey || !keyMaterial?.salt || !keyMaterial?.iv) {
    throw new Error('Please log out and log back in, then try again.');
  }

  // 1. Prove possession of the current Auth password (also satisfies recent-login).
  try {
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
  } catch (err) {
    throw mapAuthError(err);
  }

  // 2. Decrypt existing private key with the current password; verify identity.
  let privateKeyHex;
  try {
    privateKeyHex = await decryptPrivateKey(currentPassword, keyMaterial);
  } catch (_) {
    throw new Error('Current password is incorrect.');
  }

  if (!verifyKeyPair(privateKeyHex, keyMaterial.publicKey)) {
    throw new Error('Something went wrong. Please try again or contact support.');
  }

  // If this tab already has a session key, it must be the same key (detect corruption / mix-ups).
  const sessionKey = getPrivateKeyFromSession();
  if (sessionKey && sessionKey.toLowerCase() !== privateKeyHex.toLowerCase()) {
    throw new Error('Please log out and log back in, then try again.');
  }

  // 3. Re-wrap under the new password (new salt/IV). Public key unchanged.
  const newWrap = await wrapPrivateKeyWithPassword(privateKeyHex, newPassword);
  const verifyHex = await decryptPrivateKey(newPassword, newWrap);
  if (verifyHex.toLowerCase() !== privateKeyHex.toLowerCase()) {
    throw new Error('Something went wrong. Please try again.');
  }
  if (!verifyKeyPair(verifyHex, keyMaterial.publicKey)) {
    throw new Error('Something went wrong. Please try again.');
  }

  const previousWrap = {
    encPrivKey: keyMaterial.encPrivKey,
    salt: keyMaterial.salt,
    kdfParams: keyMaterial.kdfParams,
    iv: keyMaterial.iv,
    keyVersion: keyMaterial.keyVersion ?? 1,
  };

  const updatedAt = new Date().toISOString();
  const nextFields = {
    encPrivKey: newWrap.encPrivKey,
    salt: newWrap.salt,
    kdfParams: newWrap.kdfParams,
    iv: newWrap.iv,
    keyVersion: (keyMaterial.keyVersion ?? 1) + 1,
    updatedAt,
  };

  const auditorRef = doc(db, AUDITORS_COLLECTION, auditorDocId);

  // 4. Persist new wrap first so Auth+Firestore stay recoverable if Auth fails (we roll back).
  await updateDoc(auditorRef, nextFields);

  // 5. Update Firebase Auth password; roll back Firestore wrap on failure.
  try {
    await updatePassword(user, newPassword);
  } catch (authErr) {
    try {
      await updateDoc(auditorRef, {
        ...previousWrap,
        updatedAt: new Date().toISOString(),
      });
    } catch (rollbackErr) {
      console.error('[passwordService] Auth update failed and Firestore rollback failed:', authErr, rollbackErr);
      throw new Error(
        'Could not change your password. Please contact support before trying again.'
      );
    }
    throw mapAuthError(authErr);
  }

  // 6. Keep this tab's session key available under the new Auth password.
  storePrivateKeyInSession(privateKeyHex);

  return nextFields;
}
